import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import apiService from "../services/apiService";
import toast from "react-hot-toast";
import { resolveChainLogoCandidates } from "../utils/chainLogoResolver";

const RPC_GATEWAY_URL =
  process.env.REACT_APP_RPC_GATEWAY_URL || "http://localhost:8545";

const FILTER_LABELS = {
  all: "All",
  evm: "EVM",
  websocket: "WebSocket",
  tendermint: "Tendermint",
};

function getChainTypeLabel(chain) {
  if (
    chain.chain_type === "tendermint" ||
    chain.chain_type === "tendermint_rest" ||
    chain.chain_type === "tendermint_grpc"
  )
    return "Tendermint";
  if (chain.chain_type === "evm") return "EVM";
  if (chain.chain_type === "evm_ws") return "WebSocket";
  return "Other";
}

const EVM_QUICK_EXAMPLE = (url) => `curl -X POST "${url}/story-mainnet/YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"jsonrpc":"2.0","method":"eth_blockNumber","params":[],"id":1}'`;

const TENDERMINT_QUICK_EXAMPLE = (url) => `curl "${url}/akash-mainnet/YOUR_API_KEY/status"

curl "${url}/akash-mainnet/YOUR_API_KEY/block"`;

function ChainLogoBadge({ chain }) {
  const name = chain.name || "Network";
  const firstLetter = name.charAt(0).toUpperCase();
  const logoCandidates = resolveChainLogoCandidates(chain);
  const [logoFailed, setLogoFailed] = useState(false);
  const [logoIndex, setLogoIndex] = useState(0);

  useEffect(() => {
    setLogoFailed(false);
    setLogoIndex(0);
  }, [chain.id, chain.slug, chain.name, logoCandidates.length]);

  const logoUrl = logoCandidates[logoIndex] || null;

  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-sm border border-white/10 bg-[#0c0c0c]">
      {logoUrl && !logoFailed ? (
        <img
          src={logoUrl}
          alt={name}
          className="h-8 w-8 object-contain p-0.5 rounded-full"
          loading="lazy"
          decoding="async"
          onError={() => {
            const next = logoIndex + 1;
            if (next < logoCandidates.length) {
              setLogoIndex(next);
              return;
            }
            setLogoFailed(true);
          }}
        />
      ) : (
        <span className="font-title text-xl font-bold text-primary">
          {firstLetter}
        </span>
      )}
    </div>
  );
}

/** Landing-aligned badges: primary accent for EVM, neutral surfaces otherwise. */
function getChainTypeColor(typeLabel) {
  switch (typeLabel) {
    case "EVM":
      return "border-primary/30 bg-primary/10 text-primary";
    case "Tendermint":
      return "border-white/15 bg-white/[0.06] text-white/80";
    case "WebSocket":
      return "border-white/15 bg-white/[0.08] text-white/85";
    default:
      return "border-white/10 bg-white/[0.04] text-white/60";
  }
}

/**
 * Group chain rows by "family" so multi-variant chains (akash-mainnet,
 * akash-mainnet-rest, akash-rest, akash-mainnet-grpc, akash-grpc) collapse
 * to a single card with a variant picker.
 */
function getFamilyKey(slug) {
  let s = (slug || "").toLowerCase();
  // strip combined suffix first (e.g. -mainnet-rest), then single suffix
  s = s.replace(/-(mainnet|testnet|sepolia|goerli|holesky|devnet)-(rest|grpc|ws|websocket)$/i, "");
  s = s.replace(/-(rest|grpc|ws|websocket|mainnet|sepolia|testnet|goerli|holesky|devnet)$/i, "");
  // alias legacy / divergent / bare-testnet slugs onto the canonical family.
  // Bare testnet slugs (sepolia, holesky, goerli — no eth- prefix) ARE
  // Ethereum testnets and should land on the Ethereum card, not float in
  // their own family.
  const aliases = {
    eth: "ethereum",
    sepolia: "ethereum",
    goerli: "ethereum",
    holesky: "ethereum",
    avax: "avalanche",
  };
  return aliases[s] || s;
}

function deriveNetwork(slug) {
  const s = (slug || "").toLowerCase();
  if (/sepolia/.test(s)) return "sepolia";
  if (/goerli/.test(s)) return "goerli";
  if (/holesky/.test(s)) return "holesky";
  if (/testnet/.test(s)) return "testnet";
  if (/devnet/.test(s)) return "devnet";
  return "mainnet";
}

/**
 * Within a family, multiple chain rows often refer to the same network +
 * transport (e.g. `bsc` AND `bsc-mainnet`, both EVM mainnet; `eth` +
 * `eth-mainnet` + `ethereum`). Bucket by (chain_type, network) and pick
 * ONE preferred representative per bucket. Preference order:
 *   1. active beats inactive
 *   2. slug with an explicit -mainnet/-sepolia/etc suffix beats bare slug
 *   3. longer slug as final tiebreaker (more descriptive)
 */
function dedupeVariants(variants) {
  const score = (v) => {
    let s = 0;
    if (v.active !== false) s += 1000;
    if (/-(mainnet|testnet|sepolia|goerli|holesky|devnet)/i.test(v.slug)) s += 100;
    s += (v.slug || "").length;
    return s;
  };
  const buckets = new Map();
  for (const v of variants) {
    const key = `${v.chain_type}:${deriveNetwork(v.slug)}`;
    if (!buckets.has(key)) buckets.set(key, []);
    buckets.get(key).push(v);
  }
  return Array.from(buckets.values()).map(
    (bucket) => bucket.sort((a, b) => score(b) - score(a))[0],
  );
}

function getFamilyDisplayName(variants) {
  // Prefer a "Mainnet" variant's name and strip qualifier words to derive
  // the family label ("Akash Mainnet" → "Akash").
  const mainnet =
    variants.find(
      (v) => /mainnet/i.test(v.slug) && !/-(rest|grpc|ws|websocket)$/i.test(v.slug),
    ) || variants[0];
  const base = (mainnet.name || mainnet.slug || "").trim();
  const stripped = base
    .replace(/\b(Mainnet|Testnet|Sepolia|Goerli|Holesky|Devnet|REST|gRPC|WebSocket|WS)\b/gi, "")
    .replace(/\s+/g, " ")
    .trim();
  return stripped || base || mainnet.slug;
}

function networkLabel(network) {
  if (!network) return "Mainnet";
  return network.charAt(0).toUpperCase() + network.slice(1);
}

function getTransportLabel(chain) {
  if (chain.chain_type === "evm_ws") return "WSS";
  if (chain.chain_type === "tendermint_grpc") return "gRPC";
  if (chain.chain_type === "tendermint_rest") return "REST";
  return "HTTPS";
}

function variantSortOrder(chain) {
  // Mainnet first, then REST/WS/gRPC; testnets after; sorted alphabetically within.
  const slug = (chain.slug || "").toLowerCase();
  const isMainnet = /mainnet/i.test(slug) || !/(sepolia|testnet|goerli|holesky|devnet)/i.test(slug);
  let proto = 0;
  if (chain.chain_type === "tendermint_rest") proto = 1;
  else if (chain.chain_type === "evm_ws") proto = 2;
  else if (chain.chain_type === "tendermint_grpc") proto = 3;
  return (isMainnet ? 0 : 10) + proto;
}

/**
 * Consolidated card for one chain family. Renders a variant picker
 * (mainnet / REST / WS / gRPC / testnets) and reflects the selected
 * variant's transport label, status, and x-chain header value.
 */
function FamilyCard({ family, index }) {
  const sortedVariants = useMemo(
    () =>
      [...family.variants].sort(
        (a, b) =>
          variantSortOrder(a) - variantSortOrder(b) ||
          (a.slug || "").localeCompare(b.slug || ""),
      ),
    [family],
  );

  // Build the network → transport variants matrix. Each cell is one
  // chain row; the user picks "Mainnet" via the dropdown then "WSS" via
  // a tag chip, which maps to a specific variant slug.
  const variantsByNetwork = useMemo(() => {
    const m = new Map();
    for (const v of sortedVariants) {
      const net = deriveNetwork(v.slug);
      if (!m.has(net)) m.set(net, []);
      m.get(net).push(v);
    }
    return m;
  }, [sortedVariants]);

  const networks = useMemo(() => {
    const order = { mainnet: 0, sepolia: 1, holesky: 2, goerli: 3, testnet: 4, devnet: 5 };
    return [...variantsByNetwork.keys()].sort(
      (a, b) => (order[a] ?? 99) - (order[b] ?? 99) || a.localeCompare(b),
    );
  }, [variantsByNetwork]);

  const defaultSlug = (sortedVariants.find((v) => v.active !== false) || sortedVariants[0]).slug;
  const [selectedSlug, setSelectedSlug] = useState(defaultSlug);
  const selected = sortedVariants.find((v) => v.slug === selectedSlug) || sortedVariants[0];
  const selectedNetwork = deriveNetwork(selected.slug);
  const transportsForNetwork = variantsByNetwork.get(selectedNetwork) || [];

  // When user switches network, jump to the first variant of that network.
  const switchNetwork = (network) => {
    const first = variantsByNetwork.get(network) || [];
    if (first[0]) setSelectedSlug(first[0].slug);
  };

  const typeLabel = getChainTypeLabel(selected);
  const transport = getTransportLabel(selected);

  // Weight multiplier (Solana etc) is per-chain; surface on the selected
  // variant only.
  const override = Number(selected.weight_override) || 0;
  const multiplier = override > 0 ? override / 100 : 1;
  const hasMultiplier = multiplier !== 1;

  return (
    <motion.article
      key={family.family}
      className="flex flex-col overflow-hidden rounded-sm border border-white/10 bg-landing-section transition-colors hover:border-white/[0.14]"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: Math.min(index * 0.03, 0.4) }}
    >
      <div className="flex flex-1 flex-col p-5 sm:p-6">
        <div className="mb-4 flex items-start justify-between gap-3">
          <ChainLogoBadge chain={selected} />
          <span
            className={`font-secondary shrink-0 rounded-sm border px-2.5 py-1 text-xs font-semibold ${getChainTypeColor(typeLabel)}`}
          >
            {typeLabel}
          </span>
        </div>

        <h3 className="font-title text-lg font-bold text-white">
          {family.displayName}
        </h3>

        {/* Network selector — custom-styled wrapper around <select> so we
            keep native a11y / keyboard handling, but with our own chevron
            icon and dark surface that matches the rest of the dashboard.
            Hidden entirely when there's only one network (no choice). */}
        {networks.length > 1 ? (
          <div className="relative mt-3">
            <select
              value={selectedNetwork}
              onChange={(e) => switchNetwork(e.target.value)}
              className="font-secondary w-full cursor-pointer appearance-none rounded-sm border border-white/15 bg-[#0c0c0c] px-3 py-2 pr-9 text-sm text-white/85 outline-none transition-colors hover:border-white/25 focus:border-primary/60"
              aria-label={`Network for ${family.displayName}`}
            >
              {networks.map((n) => (
                <option key={n} value={n} className="bg-[#0c0c0c] text-white">
                  {networkLabel(n)}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/45"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        ) : (
          <p className="font-secondary mt-3 text-sm text-white/55">
            {networkLabel(selectedNetwork)}
          </p>
        )}

        {/* Transport tags — one chip per available transport for the
            selected network. Click to switch the active variant. Single
            transport is rendered as a static badge (no interaction
            affordance when there's nothing to choose). */}
        <div className="mt-3 flex flex-wrap items-center gap-1.5">
          {transportsForNetwork.length > 1 ? (
            transportsForNetwork.map((v) => {
              const t = getTransportLabel(v);
              const isActive = v.slug === selectedSlug;
              return (
                <button
                  key={v.slug}
                  type="button"
                  onClick={() => setSelectedSlug(v.slug)}
                  className={`font-secondary rounded-sm border px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider transition-colors ${
                    isActive
                      ? "border-primary/40 bg-primary/15 text-primary"
                      : "border-white/15 bg-white/[0.04] text-white/55 hover:border-white/30 hover:text-white/85"
                  }`}
                  aria-pressed={isActive}
                  title={`${t} — ${v.slug}`}
                >
                  {t}
                </button>
              );
            })
          ) : (
            <span className="font-secondary rounded-sm border border-white/15 bg-white/[0.06] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-white/75">
              {transport}
            </span>
          )}
          {hasMultiplier && (
            <Link
              to="/documentation#request-weights"
              onClick={(e) => e.stopPropagation()}
              className="font-secondary ml-auto inline-flex items-center gap-1 rounded-sm border border-amber-500/30 bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-200 transition-colors hover:bg-amber-500/20"
              title="Each request on this chain consumes more than 1 CU. Click to learn how it affects your quota."
            >
              {multiplier % 1 === 0 ? `${multiplier}×` : `${multiplier.toFixed(2)}×`} CU
            </Link>
          )}
        </div>

        <div className="mt-4 rounded-sm border border-white/[0.06] bg-[#0c0c0c] p-3">
          <p className="font-secondary text-xs text-white/45">
            Access via RPC gateway
          </p>
          <p className="font-mono mt-1 text-sm text-white/75">
            x-chain: {selected.slug}
          </p>
        </div>
      </div>

      <div className="border-t border-white/[0.06] bg-[#0c0c0c]/80 px-5 py-3 sm:px-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <div
              className={`h-2 w-2 shrink-0 rounded-full ${
                selected.active !== false
                  ? "bg-emerald-400/90"
                  : "bg-red-400/90"
              }`}
              aria-hidden
            />
            <span className="font-secondary text-xs text-white/55">
              {selected.active !== false ? "Active" : "Inactive"}
            </span>
            <span className="font-secondary text-xs text-white/35">
              · {sortedVariants.length} {sortedVariants.length === 1 ? "variant" : "variants"}
            </span>
          </div>
          <span className="truncate max-w-[50%] text-right font-mono text-xs text-white/40">
            x-chain: {selected.slug}
          </span>
        </div>
      </div>
    </motion.article>
  );
}

const Nodes = () => {
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [quickStartOpen, setQuickStartOpen] = useState(false);
  const quickStartRef = useRef(null);
  const quickStartPanelRef = useRef(null);
  const [quickStartFixedStyle, setQuickStartFixedStyle] = useState({
    top: 0,
    right: 12,
    width: 360,
  });

  useEffect(() => {
    loadChains();
  }, []);

  const updateQuickStartPosition = useCallback(() => {
    const el = quickStartRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const maxW = Math.min(640, window.innerWidth - 24);
    setQuickStartFixedStyle({
      top: r.bottom + 8,
      right: Math.max(12, window.innerWidth - r.right),
      width: maxW,
    });
  }, []);

  useLayoutEffect(() => {
    if (!quickStartOpen) return;
    updateQuickStartPosition();
  }, [quickStartOpen, updateQuickStartPosition]);

  useEffect(() => {
    if (!quickStartOpen) return;
    const onPointerDown = (e) => {
      const inTrigger = quickStartRef.current?.contains(e.target);
      const inPanel = quickStartPanelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) {
        setQuickStartOpen(false);
      }
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setQuickStartOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown, { passive: true });
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", updateQuickStartPosition);
    window.addEventListener("scroll", updateQuickStartPosition, true);

    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", updateQuickStartPosition);
      window.removeEventListener("scroll", updateQuickStartPosition, true);
    };
  }, [quickStartOpen, updateQuickStartPosition]);

  const loadChains = async () => {
    try {
      setLoading(true);
      const data = await apiService.getChains();
      setChains(data);
    } catch (error) {
      console.error("Error loading chains:", error);
      toast.error("Failed to load blockchain networks");
    } finally {
      setLoading(false);
    }
  };

  const { tendermintChains, evmChains, wsChains, chainsByTypeFilter } =
    useMemo(() => {
      const tendermint = chains.filter(
        (c) =>
          c.chain_type === "tendermint" ||
          c.chain_type === "tendermint_rest" ||
          c.chain_type === "tendermint_grpc",
      );
      const evm = chains.filter((c) => c.chain_type === "evm");
      const ws = chains.filter((c) => c.chain_type === "evm_ws");
      let byFilter = chains;
      if (filter === "tendermint") byFilter = tendermint;
      else if (filter === "evm") byFilter = evm;
      else if (filter === "websocket") byFilter = ws;
      return {
        tendermintChains: tendermint,
        evmChains: evm,
        wsChains: ws,
        chainsByTypeFilter: byFilter,
      };
    }, [chains, filter]);

  const displayedChains = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return chainsByTypeFilter;
    return chainsByTypeFilter.filter((chain) => {
      const name = (chain.name || "").toLowerCase();
      const slug = (chain.slug || "").toLowerCase();
      const typeLabel = getChainTypeLabel(chain).toLowerCase();
      const protocolMatch = (chain.protocols || []).some((p) =>
        String(p.full_name || p.name || "")
          .toLowerCase()
          .includes(q),
      );
      return (
        name.includes(q) ||
        slug.includes(q) ||
        typeLabel.includes(q) ||
        protocolMatch
      );
    });
  }, [chainsByTypeFilter, searchQuery]);

  // Consolidate chain rows into families (akash-mainnet + akash-rest +
  // akash-grpc + ... → one "Akash" card with a variant picker). Searching
  // / type-filtering still runs against raw chain rows above; the grouping
  // is purely a render concern.
  const displayedFamilies = useMemo(() => {
    const map = new Map();
    for (const chain of displayedChains) {
      const key = getFamilyKey(chain.slug);
      if (!map.has(key)) map.set(key, { family: key, variants: [] });
      map.get(key).variants.push(chain);
    }
    return Array.from(map.values())
      .map((f) => {
        const deduped = dedupeVariants(f.variants);
        return {
          ...f,
          variants: deduped,
          displayName: getFamilyDisplayName(deduped),
        };
      })
      .sort((a, b) => a.displayName.localeCompare(b.displayName));
  }, [displayedChains]);

  const statCard = (label, value, delay) => (
    <motion.div
      className="rounded-sm border border-white/10 bg-landing-section p-5 sm:p-6"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <div className="flex items-center justify-between gap-3">
        <div>
          <p className="font-secondary text-sm text-white/65">{label}</p>
          <p className="font-title mt-1 text-2xl font-bold tracking-tight text-white sm:text-3xl">
            {value}
          </p>
        </div>
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-sm border border-white/10 bg-[#0c0c0c] text-primary">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9"
            />
          </svg>
        </div>
      </div>
    </motion.div>
  );

  if (loading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center bg-[#0d0d0d]">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0d0d0d]">
      <header className="sticky top-0 z-20 border-b border-white/[0.06] bg-[#0d0d0d]">
        <div className="px-3 py-3 sm:px-6 sm:py-3.5 lg:px-8">
          <div className="flex min-w-0 items-center gap-2 sm:gap-3 lg:gap-4">
            <div className="hidden min-w-0 shrink-0 md:block lg:max-w-[13rem]">
              <h1 className="font-title truncate text-lg font-bold tracking-tight text-white lg:text-xl">
                Blockchain nodes
              </h1>
              <p className="font-secondary truncate text-xs text-white/55 lg:text-sm lg:text-white/65">
                {chains.length} networks · RPC gateway
              </p>
            </div>
            <div className="shrink-0 md:hidden">
              <h1 className="font-title text-base font-bold tracking-tight text-white">
                Nodes
              </h1>
            </div>

            <div className="min-w-0 flex-1">
              <label htmlFor="nodes-search" className="sr-only">
                Search networks
              </label>
              <div className="relative">
                <span
                  className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-white/40 sm:pl-3"
                  aria-hidden
                >
                  <svg
                    className="h-4 w-4 shrink-0 sm:h-[18px] sm:w-[18px]"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    />
                  </svg>
                </span>
                <input
                  id="nodes-search"
                  type="search"
                  autoComplete="off"
                  placeholder="Search name, slug, type…"
                  title={
                    searchQuery.trim()
                      ? `${displayedFamilies.length} result${displayedFamilies.length !== 1 ? "s" : ""}`
                      : undefined
                  }
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="font-secondary w-full rounded-sm border border-white/15 bg-[#0c0c0c] py-2 pl-9 pr-9 text-sm text-white shadow-inner shadow-black/20 placeholder:text-white/40 hover:border-white/25 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/30 sm:py-2.5 sm:pl-10 sm:pr-10 sm:text-[15px]"
                />
                {searchQuery ? (
                  <button
                    type="button"
                    onClick={() => setSearchQuery("")}
                    className="font-secondary absolute inset-y-0 right-0 flex items-center pr-2 text-xs font-semibold text-white/50 hover:text-white sm:pr-3 sm:text-sm"
                    aria-label="Clear search"
                  >
                    Clear
                  </button>
                ) : null}
              </div>
            </div>

            <div className="flex shrink-0 items-center gap-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] sm:gap-1.5 [&::-webkit-scrollbar]:hidden">
              <span className="font-secondary hidden whitespace-nowrap text-xs text-white/55 lg:inline lg:text-sm">
                Filter
              </span>
              {["all", "evm", "websocket", "tendermint"].map((f) => (
                <motion.button
                  key={f}
                  type="button"
                  onClick={() => setFilter(f)}
                  className={`font-secondary shrink-0 rounded-sm px-2 py-1.5 text-[11px] font-semibold transition-colors sm:px-2.5 sm:text-xs md:px-3 md:py-2 md:text-sm ${
                    filter === f
                      ? "border border-primary bg-primary font-bold text-[#0a0a0a] hover:bg-primary-hover"
                      : "border border-white/15 bg-transparent text-white/80 hover:bg-white/[0.06]"
                  }`}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {FILTER_LABELS[f] || f}
                </motion.button>
              ))}
              <div className="relative shrink-0" ref={quickStartRef}>
                <button
                  type="button"
                  onClick={() => setQuickStartOpen((o) => !o)}
                  aria-expanded={quickStartOpen}
                  aria-haspopup="dialog"
                  className={`font-secondary inline-flex min-h-[36px] items-center gap-1.5 whitespace-nowrap rounded-sm border px-2.5 py-1.5 text-[11px] font-bold transition-colors sm:min-h-[40px] sm:gap-2 sm:px-4 sm:py-2 sm:text-sm ${
                    quickStartOpen
                      ? "border-primary bg-primary-hover text-[#0a0a0a] ring-1 ring-primary/35"
                      : "border-primary bg-primary text-[#0a0a0a] hover:bg-primary-hover"
                  }`}
                >
                  <span className="hidden sm:inline">Quick start</span>
                  <span className="sm:hidden">Start</span>
                  <svg
                    className={`h-3.5 w-3.5 shrink-0 text-[#0a0a0a]/80 transition-transform sm:h-4 sm:w-4 ${quickStartOpen ? "rotate-180" : ""}`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                    aria-hidden
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {typeof document !== "undefined" &&
        createPortal(
          <AnimatePresence>
            {quickStartOpen ? (
              <motion.div
                key="quickstart-panel"
                ref={quickStartPanelRef}
                role="dialog"
                aria-label="Quick start examples"
                style={{
                  position: "fixed",
                  top: quickStartFixedStyle.top,
                  right: quickStartFixedStyle.right,
                  width: quickStartFixedStyle.width,
                  zIndex: 200,
                  maxHeight: "min(70vh, 32rem)",
                }}
                className="overflow-y-auto rounded-sm border border-white/10 bg-[#0c0c0c] p-4 shadow-[0_24px_56px_rgba(0,0,0,0.65)] sm:p-5"
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.15 }}
              >
                <h3 className="font-title text-lg font-bold tracking-tight text-white sm:text-xl">
                  Quick start
                </h3>
                <p className="font-secondary mt-3 border-b border-white/10 pb-4 text-sm leading-relaxed text-white/65">
                  <span className="font-semibold text-primary">Note:</span>{" "}
                  Replace{" "}
                  <code className="rounded-sm bg-[#131313] px-1.5 py-0.5 font-mono text-xs text-white/85">
                    YOUR_API_KEY
                  </code>{" "}
                  and set header{" "}
                  <code className="rounded-sm bg-[#131313] px-1.5 py-0.5 font-mono text-xs text-primary">
                    x-chain
                  </code>{" "}
                  to your network slug.
                </p>

                <div className="mt-5 space-y-5">
                  <div className="rounded-sm border border-white/10 bg-[#131313] p-4">
                    <div className="font-secondary mb-3 text-sm font-semibold text-white">
                      EVM (JSON-RPC)
                    </div>
                    <div className="font-secondary mb-2 text-xs text-white/45">
                      Example cURL
                    </div>
                    <div className="overflow-x-auto rounded-sm border border-white/15 bg-[#0c0c0c] p-3 ring-1 ring-transparent focus-within:border-primary focus-within:ring-primary/25">
                      <pre className="font-mono text-[11px] leading-relaxed text-white/80 whitespace-pre-wrap sm:text-sm">
                        {EVM_QUICK_EXAMPLE(RPC_GATEWAY_URL)}
                      </pre>
                    </div>
                  </div>

                  <div className="rounded-sm border border-white/10 bg-[#131313] p-4">
                    <div className="font-secondary mb-3 text-sm font-semibold text-white">
                      Tendermint (REST)
                    </div>
                    <div className="font-secondary mb-2 text-xs text-white/45">
                      Example cURL
                    </div>
                    <div className="overflow-x-auto rounded-sm border border-white/15 bg-[#0c0c0c] p-3 ring-1 ring-transparent focus-within:border-primary focus-within:ring-primary/25">
                      <pre className="font-mono text-[11px] leading-relaxed text-white/80 whitespace-pre-wrap sm:text-sm">
                        {TENDERMINT_QUICK_EXAMPLE(RPC_GATEWAY_URL)}
                      </pre>
                    </div>
                  </div>
                </div>

                <div className="mt-5 flex flex-col gap-3 border-t border-white/10 pt-4 sm:flex-row sm:justify-end sm:gap-3">
                  <button
                    type="button"
                    onClick={() => setQuickStartOpen(false)}
                    className="font-secondary rounded-sm border border-white/15 bg-transparent px-4 py-2.5 text-sm font-semibold text-white/80 transition-colors hover:bg-white/[0.06] hover:text-white"
                  >
                    Close
                  </button>
                  <button
                    type="button"
                    onClick={() => setQuickStartOpen(false)}
                    className="font-secondary rounded-sm border border-primary bg-primary px-5 py-2.5 text-sm font-bold text-[#0a0a0a] transition-colors hover:bg-primary-hover"
                  >
                    Done
                  </button>
                </div>
              </motion.div>
            ) : null}
          </AnimatePresence>,
          document.body,
        )}

      <main className="px-4 py-6 sm:px-8 sm:py-8">
        <div className="">
          <div className="mb-8 grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5">
            {statCard("EVM networks", evmChains.length, 0)}
            {statCard("Tendermint networks", tendermintChains.length, 0.05)}
            {statCard("Total networks", chains.length, 0.1)}
          </div>

          <div className="mb-8 rounded-sm border border-white/10 bg-landing-section p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm border border-white/10 bg-[#0c0c0c] text-primary">
                <svg
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  aria-hidden
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-title text-lg font-bold text-white">
                  RPC gateway access
                </h3>
                <p className="font-secondary mt-2 text-sm text-white/65">
                  Use your API key with the unified gateway; set{" "}
                  <code className="text-white/90">x-chain</code> to the slug
                  below.
                </p>
                <div className="mt-3 rounded-sm border border-white/15 bg-[#0c0c0c] p-3">
                  <code className="font-mono text-sm text-primary break-all">
                    {RPC_GATEWAY_URL}
                  </code>
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {displayedChains.length === 0 ? (
              <div className="col-span-full rounded-sm border border-white/10 bg-landing-section px-6 py-12 text-center">
                <p className="font-title text-base font-bold text-white">
                  No networks match
                </p>
                <p className="font-secondary mt-2 text-sm text-white/55">
                  Try another search term or change the type filter.
                </p>
              </div>
            ) : null}
            {displayedFamilies.map((family, index) => (
              <FamilyCard key={family.family} family={family} index={index} />
            ))}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Nodes;
